import {
  Box,
  CloseButton,
  Dialog,
  Input,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useContext, useEffect, useMemo, useState } from "react";

import TablesStateContext from "@/contexts/TablesStateContext";
import type { PortalContext } from "@/contexts/MerchantContext";
import { usePortalContext } from "@/hooks/usePortalContext";
import { usePortalContexts } from "@/hooks/usePortalContexts";
import ContextAvatar from "./ContextAvatar";

type Props = {
  open: boolean;
  onClose: () => void;
};

type RowProps = {
  displayName: string;
  kind: "org" | "merchant";
  secondary?: string;
  action?: string;
  isActive: boolean;
  onSelect: () => void;
};

const ContextRow = ({
  displayName,
  kind,
  secondary,
  action,
  isActive,
  onSelect,
}: RowProps): JSX.Element => (
  <Box
    display="flex"
    alignItems="center"
    gap={3}
    px={3}
    py={2}
    borderRadius="md"
    cursor="pointer"
    bg={isActive ? "gray.100" : undefined}
    _hover={{ bg: "gray.50" }}
    onClick={onSelect}
  >
    <ContextAvatar displayName={displayName} kind={kind} />
    <Box minW={0} flex="1">
      <Text fontSize="sm" fontWeight={600} truncate>
        {displayName}
      </Text>
      {secondary && (
        <Text fontSize="xs" color="gray.500" truncate>
          {secondary}
        </Text>
      )}
    </Box>
    {action && (
      <Text fontSize="xs" color="gray.500" flexShrink={0}>
        {action}
      </Text>
    )}
  </Box>
);

const GroupHeading = ({ children }: React.PropsWithChildren): JSX.Element => (
  <Text
    fontSize="10px"
    fontWeight={700}
    letterSpacing="0.08em"
    color="gray.500"
    px={3}
    pt={2}
    pb={1}
  >
    {children}
  </Text>
);

const normalize = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const rank = (
  query: string,
  primary: string[],
  fallback: string[] = []
): number | null => {
  const fields = primary.map(normalize);
  if (fields.some((f) => f === query)) return 0;
  if (fields.some((f) => f.startsWith(query))) return 1;
  if (fields.some((f) => f.includes(query))) return 2;
  if (fallback.some((f) => normalize(f).includes(query))) return 3;
  return null;
};

type Scored<T> = { item: T; score: number };

const score = <T,>(
  items: T[],
  query: string,
  scorer: (item: T, query: string) => number | null
): Scored<T>[] =>
  items
    .map((item) => ({ item, score: scorer(item, query) }))
    .filter((r): r is Scored<T> => r.score !== null)
    .sort((a, b) => a.score - b.score);

const pick = <T,>(scored: Scored<T>[], exactOnly: boolean): T[] =>
  scored.filter((r) => !exactOnly || r.score === 0).map((r) => r.item);

const plural = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`;

const SelectContextModal = ({ open, onClose }: Props): JSX.Element => {
  const { context, setContext } = usePortalContext();
  const { resetAllTables } = useContext(TablesStateContext);
  const { orgs, merchants } = usePortalContexts();
  const [query, setQuery] = useState("");

  // A stale query must not greet the next open.
  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  const { visibleOrgs, visibleMerchants } = useMemo(() => {
    const q = normalize(query);
    if (!q) return { visibleOrgs: orgs, visibleMerchants: merchants };
    const scoredOrgs = score(orgs, q, (org, q) =>
      rank(q, [org.displayName, org.id])
    );
    const scoredMerchants = score(merchants, q, (m, q) =>
      rank(q, [m.displayName, m.id], [m.orgDisplayName ?? ""])
    );
    const exactOnly = [...scoredOrgs, ...scoredMerchants].some(
      (r) => r.score === 0
    );
    return {
      visibleOrgs: pick(scoredOrgs, exactOnly),
      visibleMerchants: pick(scoredMerchants, exactOnly),
    };
  }, [orgs, merchants, query]);

  const handleSelect = (next: PortalContext) => {
    resetAllTables();
    setContext(next);
    onClose();
  };

  const hasResults = visibleOrgs.length > 0 || visibleMerchants.length > 0;

  // Counts what the two groups actually render, in the order they render.
  const footer = [
    ...(orgs.length > 0
      ? [plural(orgs.length, "organización", "organizaciones")]
      : []),
    plural(merchants.length, "comercio", "comercios"),
  ].join(" · ");

  return (
    <Dialog.Root open={open} onOpenChange={(e) => !e.open && onClose()}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="lg" maxWidth="560px">
            <Dialog.Header
              borderBottom="1px solid"
              borderColor="gray.200"
              p={3}
            >
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar comercio u organización"
                size="sm"
                variant="subtle"
              />
            </Dialog.Header>
            <CloseButton
              position="absolute"
              top={2}
              right={2}
              size="sm"
              onClick={onClose}
            />
            <Dialog.Body p={2} maxHeight="60vh" overflowY="auto">
              <VStack gap={0} align="stretch">
                {/* No group heading and no empty state for a user who holds no
                    orgs — they simply get a list of comercios. */}
                {visibleOrgs.length > 0 && (
                  <>
                    <GroupHeading>ORGANIZACIONES</GroupHeading>
                    {visibleOrgs.map((org) => (
                      <ContextRow
                        key={org.id}
                        displayName={org.displayName}
                        kind="org"
                        secondary={`Portafolio · ${plural(
                          org.merchantCount,
                          "comercio",
                          "comercios"
                        )}`}
                        action="Ver organización →"
                        isActive={
                          context?.type === "org" && context.id === org.id
                        }
                        onSelect={() =>
                          handleSelect({ type: "org", id: org.id })
                        }
                      />
                    ))}
                  </>
                )}
                {visibleMerchants.length > 0 && (
                  <>
                    <GroupHeading>COMERCIOS</GroupHeading>
                    {visibleMerchants.map((m) => (
                      <ContextRow
                        key={m.id}
                        displayName={m.displayName}
                        kind="merchant"
                        secondary={[m.id, m.orgDisplayName]
                          .filter(Boolean)
                          .join(" · ")}
                        isActive={
                          context?.type === "merchant" && context.id === m.id
                        }
                        onSelect={() =>
                          handleSelect({ type: "merchant", id: m.id })
                        }
                      />
                    ))}
                  </>
                )}
                {!hasResults && (
                  <Box px={3} py={6} textAlign="center">
                    <Text fontSize="sm" color="gray.500">
                      No encontramos resultados
                    </Text>
                  </Box>
                )}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer
              borderTop="1px solid"
              borderColor="gray.200"
              px={4}
              py={2}
              justifyContent="flex-start"
            >
              <Text fontSize="xs" color="gray.500">
                {footer}
              </Text>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default SelectContextModal;
